import { ReportStatusBadge, StatusBadge } from '@/components/admin/status-badge';
import { type AdminReport, admin } from '@/lib/admin-api';
import { routes } from '@/lib/routes';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

export default function AdminReportDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [report, setReport] = useState<AdminReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  const fetchReport = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const data = await admin.reports.get(id);
      setReport(data);
      setAdminNotes(data.adminNotes || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleResolve = async () => {
    if (!id || !selectedStatus) return;
    setIsResolving(true);
    try {
      const updated = await admin.reports.resolve(id, {
        status: selectedStatus,
        adminNotes: adminNotes || undefined
      });
      setReport(updated);
      setSelectedStatus('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve report');
    } finally {
      setIsResolving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue animate-spin" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="space-y-4">
        <Link
          to={routes.admin.reports}
          className="inline-flex items-center gap-2 text-sepia/70 hover:text-sepia"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('admin.reports.backToList')}
        </Link>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700">
          {error || 'Report not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to={routes.admin.reports}
        className="inline-flex items-center gap-2 text-sepia/70 hover:text-sepia"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('admin.reports.backToList')}
      </Link>

      {/* Report Info */}
      <div className="bg-white rounded-xl border border-gray-warm p-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-semibold text-ink">{t('admin.reports.reportDetails')}</h1>
          <ReportStatusBadge status={report.status} />
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-xs text-sepia/70 uppercase mb-1">{t('admin.reports.reporter')}</p>
            <Link
              to={routes.admin.user(report.reporter.id)}
              className="text-blue hover:text-blue font-medium"
            >
              {report.reporter.username}
            </Link>
            <p className="text-xs text-sepia/70">{report.reporter.email}</p>
          </div>
          <div>
            <p className="text-xs text-sepia/70 uppercase mb-1">{t('admin.reports.reported')}</p>
            <Link
              to={routes.admin.user(report.reported.id)}
              className="text-blue hover:text-blue font-medium"
            >
              {report.reported.username}
            </Link>
            <div className="mt-1">
              <StatusBadge status={report.reported.status} />
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-warm">
          <p className="text-xs text-sepia/70 uppercase mb-2">{t('admin.reports.reason')}</p>
          <p className="font-medium text-ink">{report.reason.replace('_', ' ')}</p>
        </div>

        {report.details && (
          <div className="mt-4">
            <p className="text-xs text-sepia/70 uppercase mb-2">{t('admin.reports.details')}</p>
            <p className="text-sepia whitespace-pre-wrap">{report.details}</p>
          </div>
        )}

        {report.letter && (
          <div className="mt-6 pt-6 border-t border-gray-warm">
            <p className="text-xs text-sepia/70 uppercase mb-2">
              {t('admin.reports.relatedLetter')}
            </p>
            <div className="bg-cream/50 rounded-lg p-4">
              <p className="font-medium">{report.letter.subject}</p>
            </div>
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-gray-warm text-xs text-sepia/70">
          <p>
            {t('admin.reports.createdAt')}: {new Date(report.createdAt).toLocaleString()}
          </p>
          {report.resolvedAt && (
            <p>
              {t('admin.reports.resolvedAt')}: {new Date(report.resolvedAt).toLocaleString()}
            </p>
          )}
          {report.resolvedBy && (
            <p>
              {t('admin.reports.resolvedBy')}: {report.resolvedBy.username}
            </p>
          )}
        </div>
      </div>

      {/* Resolution */}
      {report.status === 'PENDING' || report.status === 'REVIEWED' ? (
        <div className="bg-white rounded-xl border border-gray-warm p-6">
          <h2 className="text-lg font-medium text-ink mb-4">{t('admin.reports.resolve')}</h2>

          <div className="space-y-4">
            <div>
              <label htmlFor="adminNotes" className="block text-sm font-medium text-sepia mb-1">
                {t('admin.reports.adminNotes')}
              </label>
              <textarea
                id="adminNotes"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
                rows={3}
                placeholder={t('admin.reports.adminNotesPlaceholder')}
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setSelectedStatus('RESOLVED');
                  handleResolve();
                }}
                disabled={isResolving}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {t('admin.reports.markResolved')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelectedStatus('DISMISSED');
                  handleResolve();
                }}
                disabled={isResolving}
                className="px-4 py-2 bg-gray-warm/30 text-sepia rounded-lg hover:bg-gray-warm/50 transition-colors disabled:opacity-50"
              >
                {t('admin.reports.dismiss')}
              </button>
              {report.status === 'PENDING' && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedStatus('REVIEWED');
                    handleResolve();
                  }}
                  disabled={isResolving}
                  className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
                >
                  {t('admin.reports.markReviewed')}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        report.adminNotes && (
          <div className="bg-white rounded-xl border border-gray-warm p-6">
            <h2 className="text-lg font-medium text-ink mb-2">{t('admin.reports.adminNotes')}</h2>
            <p className="text-sepia whitespace-pre-wrap">{report.adminNotes}</p>
          </div>
        )
      )}
    </div>
  );
}
